export interface PasswordStrength {
  percentage: number
  color: string
  text: string
}

/**
 * Port of `Register.calculatePasswordStrength`, weights unchanged.
 *
 * This is not cosmetic: `Register.validateForm` refuses to submit when the score
 * is below 60, so the thresholds are a real gate on what passwords are accepted.
 * Note that 60 needs more than the server-side rule (8 chars, one lower, one
 * upper, one digit = 25+15+15+20 = 75, which passes; but 8 chars with only
 * lower+digit = 60, exactly at the boundary).
 */
export function calculatePasswordStrength(password: string): PasswordStrength {
  if (password.length === 0) {
    return { percentage: 0, color: '#e5e7eb', text: 'Enter password' }
  }

  let score = 0
  if (password.length >= 8) score += 25
  if (password.length >= 12) score += 10
  if (/[a-z]/.test(password)) score += 15
  if (/[A-Z]/.test(password)) score += 15
  if (/[0-9]/.test(password)) score += 20
  if (/[^A-Za-z0-9]/.test(password)) score += 15

  if (score < 30) return { percentage: score, color: '#ef4444', text: 'Weak' }
  if (score < 60) return { percentage: score, color: '#f59e0b', text: 'Fair' }
  if (score < 80) return { percentage: score, color: '#10b981', text: 'Good' }
  return { percentage: score, color: '#059669', text: 'Strong' }
}

/** `Register.isValidEmail` — the client-side check, kept as-is. */
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

/**
 * The server's own rule, from the [RegularExpression] on
 * RegisterRequestDto.Password: at least one lowercase, one uppercase, one digit.
 *
 * Checked on the client too, because the two rules in the original DISAGREED and
 * the mismatch was invisible to the user. `password123` scores exactly 60 on the
 * strength meter — passing `< 60` — but has no uppercase letter, so the server
 * rejected it; and the controller answered every rejection with a flat
 * "Invalid input data." with no field named. Registration simply failed with no
 * way to work out why.
 *
 * This adds no new requirement: it is the same regex the server has always
 * enforced, applied where the user can still act on it.
 */
export const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/

export function meetsPasswordRule(password: string): boolean {
  return PASSWORD_RULE.test(password)
}
